import React from "react";
import scopeworthLogo from "../assets/scopeworth-logo.svg";
import { ReferenceSkeleton } from "./ReferenceSkeleton.jsx";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import bundle from "virtual:developer-docs";
import { CookieConsentRoot } from "./CookieConsent.jsx";
import {
  guidePath,
  docsDescription,
  guideHref,
  operationAnchor,
  PRIMARY_GUIDES,
  OVERVIEW_CONTENT,
  publicSearchIndex,
  searchDocs,
  resolveDocsRoute,
} from "./developer-docs-model.js";
import {
  createDocsContextController,
  personalizeSnippets,
  approvedCodeExamples,
} from "./developer-docs-context.js";
import { appUrl } from "./app-origin.js";
import { USE_CASE_SLUGS } from './use-case-routes.js';
import { useCaseContent } from './use-case-content.js';
import { registerDocsCatalogue } from "./developer-docs-events.js";
import { trackDocs } from "./developer-docs-analytics.js";
import { syntaxTokens } from "./developer-docs-syntax.js";
import { DEVELOPER_FOOTER } from "./developer-footer.js";
import {
  documentationParts,
  remarkDocumentationSemantics,
} from "./developer-docs-semantics.js";
registerDocsCatalogue(bundle);

const LazyReference = React.lazy(() => import("./DeveloperReference.jsx"));
const searchIndex = publicSearchIndex(bundle);
const CodeExamples = React.createContext(new Map());
const CopyMetadata = React.createContext({});
const SelectedWorkspace = React.createContext(null);
const headingText = (children) =>
  React.Children.toArray(children)
    .map((child) =>
      React.isValidElement(child)
        ? headingText(child.props.children)
        : String(child),
    )
    .join("");
const headingId = (children) =>
  headingText(children)
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "")
    .trim()
    .replace(/\s+/g, "-");
const DOWNLOAD_FILENAME = "scopeworth-api-v1.openapi.json";
const categoryNames = {
  intelligence: "Delivery intelligence",
  administration: "Workspace & access",
  assumptions: "Assumptions",
  clients: "Clients & projects",
  commercial: "Commercial inputs",
  evidence: "Evidence & confidence",
  imports: "Imports",
  investigations: "Investigations & packages",
  reports: "Reports",
  scenarios: "Capacity scenarios",
};
const categories = [...new Set(bundle.navigation.map((op) => op.tag))].map(
  (tag) => ({
    tag,
    title: categoryNames[tag] || tag,
    operations: bundle.navigation.filter((op) => op.tag === tag),
  }),
);
function TokenText({ tokens }) {
  return tokens.map((token, index) =>
    typeof token === "string" ? (
      token
    ) : (
      <span key={index} className={`token ${token.type}`}>
        <TokenText
          tokens={
            Array.isArray(token.content) ? token.content : [token.content]
          }
        />
      </span>
    ),
  );
}
function HighlightedCode({ text, language }) {
  const tokens = React.useMemo(
    () => syntaxTokens(text, language),
    [text, language],
  );
  return (
    <code className={`language-${language}`}>
      <TokenText tokens={tokens} />
    </code>
  );
}
function EndpointNavigation({ hash }) {
  const [expanded, setExpanded] = React.useState({});
  React.useEffect(() => {
    const op = bundle.navigation.find((item) => operationAnchor(item) === hash);
    if (op) setExpanded((state) => ({ ...state, [op.tag]: true }));
  }, [hash]);
  return (
    <div className="sw-docs-endpoint-nav">
      <p>Endpoints by category</p>
      {categories.map((group) => (
        <section key={group.tag}>
          <button
            type="button"
            aria-expanded={Boolean(expanded[group.tag])}
            aria-controls={`docs-category-${group.tag}`}
            onClick={() =>
              setExpanded((state) => ({
                ...state,
                [group.tag]: !state[group.tag],
              }))
            }
          >
            <span>{group.title}</span>
            <small>{group.operations.length}</small>
            <span aria-hidden="true">{expanded[group.tag] ? "−" : "+"}</span>
          </button>
          <ul id={`docs-category-${group.tag}`} hidden={!expanded[group.tag]}>
            {group.operations.map((op) => (
              <li key={op.id}>
                <a
                  href={`/docs/api-reference${operationAnchor(op)}`}
                  aria-current={
                    hash === operationAnchor(op) ? "location" : undefined
                  }
                >
                  <span
                    className={`sw-docs-method sw-docs-method-${op.method.toLowerCase()}`}
                  >
                    {op.method}
                  </span>
                  <span>{op.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
function DocsFooter() {
  return (
    <footer className="sw-docs-footer">
      <div>
        <a href="/" className="sw-docs-brand">
          <img src={scopeworthLogo} alt="ScopeWorth" width="180" height="42" />
        </a>
        <p>Delivery evidence for agencies and their clients.</p>
      </div>
      <nav aria-label="Documentation footer">
        <div>
          <strong>{DEVELOPER_FOOTER.title}</strong>
          <a
            href={DEVELOPER_FOOTER.href}
            data-docs-event={DEVELOPER_FOOTER.event}
            data-docs-location={DEVELOPER_FOOTER.location}
            onClick={() =>
              trackDocs(DEVELOPER_FOOTER.event, {
                location: DEVELOPER_FOOTER.location,
              })
            }
          >
            {DEVELOPER_FOOTER.label}
          </a>
          <a href="/docs/api-reference">API reference</a>
          <a href="/docs/changelog">Changelog</a>
        </div>
        <div>
          <strong>Evaluate ScopeWorth</strong>
          <a href="/docs/plans-limits">Plans and limits</a>
          <a href="/pricing">Pricing</a>
          <a href="/security">Security</a>
        </div>
        <div>
          <strong>Company</strong>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/privacy#advertising-optout">Advertising preferences</a>
          <button
            type="button"
            onClick={() => window.openCookiePreferences?.()}
          >
            Cookie preferences
          </button>
        </div>
      </nav>
      <p className="sw-docs-footer-bottom">ScopeWorth Customer API · v1</p>
    </footer>
  );
}
function FirstRequest({ snippets }) {
  const [language, setLanguage] = React.useState("sh");
  const metadata = React.useContext(CopyMetadata);
  const example = snippets.find(
    (item) => item.operationId === "directory.read",
  );
  const field = { sh: "curl", javascript: "javascript", python: "python" }[
    language
  ];
  return (
    <section className="sw-docs-first-request">
      <h2>Your first API request</h2>
      <p>Discover the clients and projects available to your workspace.</p>
      <div
        role="group"
        aria-label="Example language"
        className="sw-docs-language-tabs"
      >
        {[
          ["sh", "cURL"],
          ["javascript", "JavaScript"],
          ["python", "Python"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={language === value}
            onClick={() => setLanguage(value)}
          >
            {label}
          </button>
        ))}
      </div>
      <CopyMetadata.Provider
        value={{
          ...metadata,
          operationId: "directory.read",
        }}
      >
        <CopyCode>
          <code className={`language-${language}`}>{example[field]}</code>
        </CopyCode>
      </CopyMetadata.Provider>
      <p>
        <strong>Run examples on your server.</strong> Store your API key in an
        environment variable; keep it out of browser code.
      </p>
      <a href="/docs/getting-started">
        Make your first request <span aria-hidden="true">→</span>
      </a>
    </section>
  );
}
function Overview({ snippets }) {
  const planLabels = {
    clients: "Clients",
    projects: "Projects",
    dataWindowDays: "Evidence window",
    reportsPerMonth: "Saved reports / month",
    syncFrequency: "Sync eligibility",
  };
  return (
    <>
      <h1 tabIndex="-1">{OVERVIEW_CONTENT.title}</h1>
      <p className="sw-docs-intro">{OVERVIEW_CONTENT.intro}</p>
      <p>
        {OVERVIEW_CONTENT.description}{" "}
        <strong>{OVERVIEW_CONTENT.access}</strong>
      </p>
      <div className="sw-docs-overview-columns">
        <section>
          <h2>Build an integration</h2>
          <ol className="sw-docs-start">
            <li>
              <a href="/docs/authentication">
                Create a scoped, expiring API key.
              </a>
              <p>Keep it on your server in an environment variable.</p>
            </li>
            <li>
              <a href="/docs/organizations-workspaces">
                Find your organization and workspace.
              </a>
              <p>Use verified routing; discover project and client IDs.</p>
            </li>
            <li>
              <a href="/docs/api-reference">Choose a documented operation.</a>
              <p>Review the schema, units, scopes and plan requirements.</p>
            </li>
          </ol>
          <h2>Evaluate ScopeWorth</h2>
          <p>
            Check{" "}
            <a href="/docs/plans-limits">
              <strong>plan eligibility and limits</strong>
            </a>
            , the <a href="/docs/api-reference">endpoint catalogue</a> and{" "}
            <a href="/security">security controls</a> before choosing a plan.
          </p>
        </section>
        <FirstRequest snippets={snippets} />
      </div>
      <section className="sw-docs-use-cases" aria-labelledby="api-use-cases">
        <h2 id="api-use-cases">Use Cases for the API</h2>
        <p>{OVERVIEW_CONTENT.useCases}</p>
        <ul className="sw-docs-workflows">
          <li>
            <a href="/docs/use-cases#prepare-recurring-client-reports">
              Prepare recurring client reports <span aria-hidden="true">→</span>
            </a>
          </li>
          <li>
            <a href="/docs/use-cases#review-scope-changes-before-a-client-call">
              Review scope changes before a client call{" "}
              <span aria-hidden="true">→</span>
            </a>
          </li>
          <li>
            <a href="/docs/use-cases#support-invoice-reviews-with-evidence">
              Support invoice reviews with evidence{" "}
              <span aria-hidden="true">→</span>
            </a>
          </li>
          <li>
            <a href="/docs/use-cases#connect-project-setup-and-commercial-inputs">
              Connect project setup and commercial inputs{" "}
              <span aria-hidden="true">→</span>
            </a>
          </li>
          <li>
            <a href="/docs/use-cases#bring-delivery-evidence-to-internal-investment-reviews">
              Bring delivery evidence to internal investment reviews{" "}
              <span aria-hidden="true">→</span>
            </a>
          </li>
          <li>
            <a href="/docs/use-cases#check-data-readiness-before-a-delivery-handoff">
              Check data readiness before a delivery handoff{" "}
              <span aria-hidden="true">→</span>
            </a>
          </li>
        </ul>
        <a href="/docs/use-cases">
          Explore use cases and API examples <span aria-hidden="true">→</span>
        </a>
      </section>
      <section className="sw-docs-plan-preview">
        <h2>Compare the integration essentials</h2>
        <p>{OVERVIEW_CONTENT.planIntro}</p>
        <div
          className="sw-docs-table-scroll"
          tabIndex="0"
          role="region"
          aria-label="Published plan comparison"
        >
          <table>
            <thead>
              <tr>
                <th scope="col">Allowance</th>
                {bundle.plans.plans.map((item) => (
                  <th key={item.id} scope="col">
                    {item.id[0].toUpperCase() + item.id.slice(1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(planLabels).map(([key, label]) => (
                <tr key={key}>
                  <th scope="row">{label}</th>
                  {bundle.plans.plans.map((item) => (
                    <td key={item.id}>
                      {key === "dataWindowDays"
                        ? `${item.caps[key]} days`
                        : String(item.caps[key])[0].toUpperCase() +
                          String(item.caps[key]).slice(1)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <a href="/docs/plans-limits">
          Read all plan limits and caveats <span aria-hidden="true">→</span>
        </a>
      </section>
      <section>
        <h2>Follow a complete workflow</h2>
        <ul className="sw-docs-workflows">
          {bundle.guides
            .filter((guide) => !PRIMARY_GUIDES.includes(guide.id))
            .map((guide) => (
              <li key={guide.id}>
                <a href={guidePath(guide.id)}>
                  {guide.title}
                  <span aria-hidden="true">→</span>
                </a>
              </li>
            ))}
        </ul>
      </section>
      <aside className="sw-docs-callout">
        <strong>Examples use synthetic records and a key placeholder.</strong>{" "}
        Documentation does not send business API requests. Available evidence
        and plan limits remain explicit.
      </aside>
    </>
  );
}
class ReferenceBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? (
      <p role="alert">
        The API reference could not load. Reload this page, or download the
        verified OpenAPI document below.
      </p>
    ) : (
      this.props.children
    );
  }
}
function CopyCode({ children }) {
  const [copied, setCopied] = React.useState(false);
  const code = React.Children.toArray(children).find((child) =>
    React.isValidElement(child),
  );
  const examples = React.useContext(CodeExamples);
  const metadata = React.useContext(CopyMetadata);
  const originalText = String(code?.props.children ?? "");
  const text = examples.get(originalText.trim()) ?? originalText;
  React.useEffect(() => {
    setCopied(false);
  }, [text]);
  const language = code?.props.className?.replace("language-", "") ?? "text";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      trackDocs("developer_example_copied", {
        ...metadata,
        language: {
          sh: "curl",
          bash: "curl",
          javascript: "javascript",
          python: "python",
          json: "json",
        }[language],
      });
    } catch {
      setCopied(false);
    }
  };
  const content = (
    <div className="sw-docs-code ph-no-capture ph-no-record">
      <div className="sw-docs-code-bar">
        <span>
          {language === "bash" || language === "sh"
            ? "cURL / shell"
            : { javascript: "JavaScript", python: "Python", json: "JSON" }[
                language
              ] || language}
        </span>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy ${language} example`}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre>
        <HighlightedCode text={text} language={language} />
      </pre>
    </div>
  );
  return language === "json" ? (
    <details className="sw-docs-schema">
      <summary>View schema or synthetic response</summary>
      {content}
    </details>
  ) : (
    content
  );
}
function Guide({ guide }) {
  const selected = React.useContext(SelectedWorkspace);
  return (
    <article className="sw-docs-prose">
      <Markdown
        remarkPlugins={[
          remarkGfm,
          [remarkDocumentationSemantics, { selected }],
        ]}
        components={{
          h1: ({ children }) => <h1 tabIndex="-1">{children}</h1>,
          p: ({ children }) => (
            <p>
              {React.Children.map(children, (child) => {
                if (typeof child !== "string") return child;
                return child
                  .split(
                    /(Required key scope:|Current creator action:|Eligible plans:|Success statuses:|Operation:)/,
                  )
                  .map((part, index) =>
                    index % 2 ? <strong key={index}>{part}</strong> : part,
                  );
              })}
            </p>
          ),
          pre: CopyCode,
          h2: ({ children }) => <h2 id={headingId(children)}>{children}</h2>,
          h3: ({ children }) => <h3 id={headingId(children)}>{children}</h3>,
          a: ({ href, title, className, children }) => (
            <a
              href={guideHref(href, bundle.guides)}
              title={title}
              className={className}
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div
              className="sw-docs-table-scroll"
              tabIndex="0"
              role="region"
              aria-label="Documentation table"
            >
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {guide.markdown}
      </Markdown>
      {USE_CASE_SLUGS.some((slug) => useCaseContent(slug).guide === guidePath(guide.id)) && <section aria-label="Related business workflows"><h2>Related business workflows</h2><ul>{USE_CASE_SLUGS.map((slug) => useCaseContent(slug)).filter((item) => item.guide === guidePath(guide.id)).map((item) => <li key={item.slug}><a href={`/use-cases/${item.slug}`}>{item.title}</a></li>)}</ul></section>}
    </article>
  );
}
function DocumentationInline({ text, selected }) {
  return documentationParts(text, selected).map((part, index) =>
    part.type === "inlineCode" ? (
      <code key={index}>{part.value}</code>
    ) : part.type === "link" ? (
      <a
        key={index}
        href={part.href}
        title={part.title}
        className="ph-no-capture ph-no-record"
      >
        {part.value}
      </a>
    ) : (
      part.value
    ),
  );
}
export function DeveloperDocs({
  path = typeof window === "undefined" ? "/docs" : window.location.pathname,
}) {
  const [location, setLocation] = React.useState(() => ({
    path,
    hash: typeof window === "undefined" ? "" : window.location.hash,
  }));
  const route = resolveDocsRoute(location.path, bundle.guides);
  const navigating = React.useRef(false);
  const restoringScroll = React.useRef(false);
  const [query, setQuery] = React.useState("");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const searchDialog = React.useRef(null);
  const [drawer, setDrawer] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [plan, setPlan] = React.useState("all");
  const headerRef = React.useRef(null);
  const [headerHeight, setHeaderHeight] = React.useState(128);
  const [railWidth, setRailWidth] = React.useState(null);
  const [resizing, setResizing] = React.useState(false);
  const drag = React.useRef(null);
  const railLimits = () => ({
    min: 220,
    max: Math.max(
      220,
      Math.min(
        480,
        window.innerWidth -
          (window.innerWidth >= 1280 && route.type === "guide" ? 620 : 420),
      ),
    ),
  });
  const resizeRail = (value) => {
    const { min, max } = railLimits();
    setRailWidth(Math.round(Math.max(min, Math.min(max, value))));
  };
  React.useEffect(() => {
    const resize = () => {
      if (railWidth !== null) resizeRail(railWidth);
    };
    window.addEventListener("resize", resize);
    resize();
    return () => window.removeEventListener("resize", resize);
  }, [railWidth, route.type]);
  const searchRef = React.useRef(null);
  const toggleRef = React.useRef(null);
  React.useEffect(() => {
    if (!searchOpen) {
      searchDialog.current?.close();
      return;
    }
    const previousOverflow = document.body.style.overflow;
    searchDialog.current.showModal();
    searchDialog.current.querySelector("input")?.focus({ preventScroll: true });
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      searchDialog.current?.close();
    };
  }, [searchOpen]);
  const [controller] = React.useState(() => createDocsContextController());
  const [context, setContext] = React.useState(() => controller.getSnapshot());
  React.useEffect(
    () => controller.subscribe(() => setContext(controller.getSnapshot())),
    [controller],
  );
  const snippets = React.useMemo(
    () => personalizeSnippets(bundle.snippets, context.selected),
    [context.selected],
  );
  const codeExamples = React.useMemo(
    () => approvedCodeExamples(bundle.snippets, snippets),
    [snippets],
  );
  React.useEffect(() => {
    setMounted(true);
  }, []);
  React.useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setHeaderHeight(entry.target.getBoundingClientRect().height),
    );
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);
  React.useEffect(() => {
    document.title = `${route.guide?.title || (route.type === "landing" ? "Developer Resources" : "Documentation page not found")} | ScopeWorth`;
    document.querySelector('meta[name="description"]')?.setAttribute('content', docsDescription(route.guide));
    if (route.type !== "missing") {
      trackDocs("developer_docs_viewed");
      if (route.guide)
        trackDocs("developer_guide_viewed", { guideId: route.guide.id });
    }
  }, [location.path]);
  React.useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    const saveScroll = () => {
      if (!restoringScroll.current)
        window.history.replaceState(
          {
            ...window.history.state,
            swDocsScroll: { x: window.scrollX, y: window.scrollY },
          },
          "",
        );
    };
    const update = (event) => {
      if (event.type === "popstate") {
        restoringScroll.current = true;
        navigating.current = {
          type: "history",
          scroll: event.state?.swDocsScroll,
        };
      } else if (!restoringScroll.current)
        navigating.current = { type: "anchor" };
      setLocation({
        path: window.location.pathname,
        hash: window.location.hash,
      });
    };
    window.addEventListener("popstate", update);
    window.addEventListener("hashchange", update);
    window.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("hashchange", update);
      window.removeEventListener("scroll", saveScroll);
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);
  React.useEffect(() => {
    if (location.hash && !location.hash.startsWith("#reference/")) {
      const target = document.getElementById(location.hash.slice(1));
      const details = target?.closest("details");
      if (details) details.open = true;
    }
    if (!navigating.current) return;
    setDrawer(false);
    setQuery("");
    if (location.hash.startsWith("#reference/")) setPlan("all");
    const frame = requestAnimationFrame(() => {
      const transition = navigating.current;
      if (
        transition.type === "history" &&
        transition.scroll &&
        !location.hash.startsWith("#reference/")
      ) {
        window.scrollTo(transition.scroll.x, transition.scroll.y);
        document
          .querySelector("#docs-content h1")
          ?.focus({ preventScroll: true });
      } else if (!location.hash) {
        window.scrollTo(0, 0);
        document
          .querySelector("#docs-content h1")
          ?.focus({ preventScroll: true });
      } else if (!location.hash.startsWith("#reference/"))
        document.getElementById(location.hash.slice(1))?.scrollIntoView();
      restoringScroll.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [location]);
  const navigate = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const link = event.target.closest("a[href]");
    if (
      !link ||
      link.hasAttribute("download") ||
      link.target ||
      link.getAttribute("rel")?.includes("external")
    )
      return;
    const url = new URL(link.href, window.location.href);
    if (
      url.origin !== window.location.origin ||
      url.search ||
      resolveDocsRoute(url.pathname, bundle.guides).type === "missing"
    )
      return;
    setSearchOpen(false);
    if (
      url.pathname.replace(/\/$/, "") ===
        window.location.pathname.replace(/\/$/, "") &&
      url.hash
    )
      return;
    event.preventDefault();
    setSearchOpen(false);
    if (
      url.pathname.replace(/\/$/, "") ===
        window.location.pathname.replace(/\/$/, "") &&
      url.hash === window.location.hash
    ) {
      setDrawer(false);
      setQuery("");
      window.scrollTo(0, 0);
      document
        .querySelector("#docs-content h1")
        ?.focus({ preventScroll: true });
      return;
    }
    window.history.replaceState(
      {
        ...window.history.state,
        swDocsScroll: { x: window.scrollX, y: window.scrollY },
      },
      "",
    );
    window.history.pushState(
      { swDocsScroll: { x: 0, y: 0 } },
      "",
      url.pathname + url.hash,
    );
    navigating.current = { type: "link" };
    setLocation({ path: url.pathname, hash: url.hash });
  };
  React.useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        if (searchOpen) return;
        setDrawer(false);
        setQuery("");
        if (drawer) toggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer, searchOpen]);
  const results = searchDocs(searchIndex, query);
  const detailedResults = searchDocs(searchIndex, query, searchIndex.length);
  const download = `/docs/_assets/${bundle.manifest.files.find((file) => file.kind === "openapi").path}`;
  return (
    <div
      className={`sw-docs-app${resizing ? " sw-docs-resizing" : ""}`}
      data-sw-brand-skip="true"
      onClick={navigate}
      style={{
        "--docs-header-height": `${headerHeight}px`,
        ...(railWidth === null
          ? {}
          : { "--docs-rail-width": `${railWidth}px` }),
      }}
    >
      <a className="sw-docs-skip" href="#docs-content">
        Skip to documentation
      </a>
      <header ref={headerRef} className="sw-docs-top" data-scalar-scroll-header="">
        <div className="sw-docs-top-row">
          <a href="/" className="sw-docs-wordmark">
            <img
              src={scopeworthLogo}
              alt="ScopeWorth"
              width="180"
              height="42"
            />
            <span>
              Docs <small>API v1</small>
            </span>
          </a>
          <nav aria-label="Documentation sections">
            <a href="/docs/getting-started">Get started</a>
            <a href="/docs/api-reference">API reference</a>
            <a href="/docs/plans-limits">Plans and limits</a>
          </nav>
          <a className="sw-docs-dashboard" href={appUrl("/app")}>
            Dashboard <span aria-hidden="true">↗</span>
          </a>
        </div>
        <div className="sw-docs-toolbar">
          <button
            ref={toggleRef}
            className="sw-docs-mobile-toggle"
            type="button"
            aria-expanded={drawer}
            aria-controls="docs-navigation"
            onClick={() => {
              setDrawer(!drawer);
              if (!drawer) setTimeout(() => searchRef.current?.focus(), 0);
            }}
          >
            Browse documentation
          </button>
          {context.workspaces.length > 0 ? (
            <section
              className="sw-docs-context ph-no-capture ph-no-record"
              aria-label="Verified example context"
            >
              <label htmlFor="docs-workspace">
                Organization / workspace for examples
              </label>
              <select
                id="docs-workspace"
                value={context.selected?.workspaceId || ""}
                onChange={(event) => controller.select(event.target.value)}
              >
                <option value="">Generic examples</option>
                {context.workspaces.map((item) => (
                  <option key={item.workspaceId} value={item.workspaceId}>
                    {item.organizationName} / {item.workspaceName}
                  </option>
                ))}
              </select>
              {context.selected ? (
                <>
                  <p>
                    <strong>{context.selected.effectivePlan}</strong> ·
                    Synthetic record IDs
                  </p>
                  <div className="sw-docs-context-actions">
                    <button
                      type="button"
                      onClick={() => {
                        controller.reset();
                        void controller.refresh();
                      }}
                    >
                      Clear personalized examples
                    </button>
                    {context.selected.canManageApiKeys ? (
                      <a
                        href={appUrl(context.selected.apiKeysUrl)}
                        onClick={() =>
                          trackDocs(
                            "developer_api_key_cta_clicked",
                            { personalized: true },
                            true,
                          )
                        }
                      >
                        Manage API keys
                      </a>
                    ) : (
                      <span>
                        An owner or admin manages this workspace’s API keys.
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <p>Select a workspace to personalize examples.</p>
              )}
            </section>
          ) : (
            <p className="sw-docs-generic">
              <strong>Generic examples</strong>
              <span>
                <a href={appUrl("/sign-in")}>Sign in</a> to personalize
                workspace URLs.
              </span>
            </p>
          )}
          <a
            className="sw-docs-download"
            href={download}
            download={DOWNLOAD_FILENAME}
            onClick={() => trackDocs("developer_openapi_downloaded")}
          >
            Download OpenAPI <span aria-hidden="true">↓</span>
          </a>
        </div>
      </header>
      <div
        className={`sw-docs-layout${route.type === "guide" ? " sw-docs-with-outline" : ""}${drawer ? " sw-docs-drawer-open" : ""}`}
        data-sw-brand-skip="true"
      >
        <aside
          id="docs-navigation"
          className="sw-docs-sidebar"
          aria-label="Documentation navigation"
        >
          <label htmlFor="docs-search">Search guides and endpoints</label>
          <input
            id="docs-search"
            ref={searchRef}
            type="search"
            maxLength="160"
            value={query}
            placeholder="Search documentation…"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && query.trim()) {
                event.preventDefault();
                setSearchOpen(true);
              }
            }}
            className="ph-no-capture ph-no-record"
            autoComplete="off"
          />
          <p className="sw-docs-search-hint">
            Press Enter for detailed results.
          </p>
          {query.trim() ? (
            <div className="sw-docs-search-results" aria-live="polite">
              <p>
                {results.length
                  ? `${results.length} results`
                  : "No results. Try a resource name such as imports or reports."}
              </p>
              <ul>
                {results.map((entry) => (
                  <li key={`${entry.kind}:${entry.id}`}>
                    <a href={entry.href}>{entry.title}</a>
                    <small>{entry.kind}</small>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="sw-docs-search-all"
                onClick={() => setSearchOpen(true)}
              >
                View detailed results ({detailedResults.length})
              </button>
            </div>
          ) : (
            <nav>
              <a
                href="/docs"
                aria-current={route.type === "landing" ? "page" : undefined}
              >
                Overview
              </a>
              <p>Start here</p>
              {bundle.guides
                .filter((guide) => PRIMARY_GUIDES.includes(guide.id))
                .sort(
                  (a, b) =>
                    PRIMARY_GUIDES.indexOf(a.id) - PRIMARY_GUIDES.indexOf(b.id),
                )
                .map((guide) => (
                  <a
                    key={guide.id}
                    href={guidePath(guide.id)}
                    aria-current={
                      route.guide?.id === guide.id ? "page" : undefined
                    }
                  >
                    {guide.title}
                  </a>
                ))}
              <EndpointNavigation hash={location.hash} />
              <p>Workflow guides</p>
              {bundle.guides
                .filter((guide) => !PRIMARY_GUIDES.includes(guide.id))
                .map((guide) => (
                  <a
                    key={guide.id}
                    href={guidePath(guide.id)}
                    aria-current={
                      route.guide?.id === guide.id ? "page" : undefined
                    }
                  >
                    {guide.title}
                  </a>
                ))}
            </nav>
          )}
          <p className="sw-docs-revision">
            Published API revision{" "}
            <code>{bundle.manifest.apiRevision.slice(0, 12)}</code>
          </p>
        </aside>
        <div
          className="sw-docs-resizer"
          role="separator"
          tabIndex="0"
          aria-label="Resize documentation navigation"
          aria-orientation="vertical"
          aria-controls="docs-navigation docs-content"
          aria-valuemin="220"
          aria-valuemax={mounted ? railLimits().max : 480}
          aria-valuenow={railWidth ?? 280}
          aria-valuetext={`${railWidth ?? 280} pixels. Use arrow keys to resize; Home and End for limits.`}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.currentTarget.focus();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = {
              x: event.clientX,
              width: document
                .getElementById("docs-navigation")
                .getBoundingClientRect().width,
            };
            setResizing(true);
          }}
          onPointerMove={(event) => {
            if (drag.current)
              resizeRail(drag.current.width + event.clientX - drag.current.x);
          }}
          onPointerUp={(event) => {
            drag.current = null;
            setResizing(false);
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => {
            drag.current = null;
            setResizing(false);
          }}
          onLostPointerCapture={() => {
            drag.current = null;
            setResizing(false);
          }}
          onDoubleClick={() => setRailWidth(null)}
          onKeyDown={(event) => {
            const current = document
              .getElementById("docs-navigation")
              .getBoundingClientRect().width;
            const step = event.shiftKey ? 40 : 16;
            const value = {
              ArrowLeft: current - step,
              ArrowRight: current + step,
              Home: railLimits().min,
              End: railLimits().max,
            }[event.key];
            if (value !== undefined) {
              event.preventDefault();
              resizeRail(value);
            }
          }}
        />
        <main id="docs-content" className="sw-docs-content" tabIndex="-1">
          <SelectedWorkspace.Provider value={context.selected}>
            <CodeExamples.Provider value={codeExamples}>
              <CopyMetadata.Provider
                value={{
                  guideId: route.guide?.id,
                  personalized: Boolean(context.selected),
                }}
              >
                {route.type === "landing" ? (
                  <Overview snippets={snippets} />
                ) : route.type === "missing" ? (
                  <>
                    <h1 tabIndex="-1">Documentation page not found</h1>
                    <p>This path is not part of the published bundle.</p>
                    <a href="/docs">Return to Developer Resources</a>
                  </>
                ) : route.type === "reference" ? (
                  <>
                    <h1 tabIndex="-1">API reference</h1>
                    <p>
                      Generated schemas and tested server-side examples.
                      Eligibility badges describe the current plan contract;
                      scopes, creator permissions, quotas and evidence
                      availability are checked separately.
                    </p>
                    <div className="sw-docs-filter">
                      <label htmlFor="docs-plan">
                        Display operations available on
                      </label>
                      <select
                        id="docs-plan"
                        value={plan}
                        onChange={(event) => {
                          setPlan(event.target.value);
                          trackDocs("developer_plan_filter_changed", {
                            plan: event.target.value,
                          });
                        }}
                      >
                        <option value="all">All plans</option>
                        <option value="free">Free</option>
                        <option value="entry">Entry</option>
                        <option value="growth">Growth</option>
                      </select>
                      <span>
                        Display filter only.{" "}
                        <a href="/docs/plans-limits">
                          Compare plans and limits
                        </a>
                      </span>
                    </div>
                    <section
                      className="sw-docs-endpoint-summaries"
                      aria-label="Endpoint summaries"
                    >
                      <details open={!mounted}>
                        <summary>
                          {
                            bundle.navigation.filter(
                              (op) => plan === "all" || op.plans.includes(plan),
                            ).length
                          }{" "}
                          documented operations
                        </summary>
                        <ul>
                          {bundle.navigation
                            .filter(
                              (op) => plan === "all" || op.plans.includes(plan),
                            )
                            .map((op) => (
                              <li key={op.id}>
                                <a href={operationAnchor(op)}>
                                  <code>
                                    {op.method} {op.path}
                                  </code>{" "}
                                  <strong>{op.title}</strong>
                                </a>
                                <p>
                                  {op.scope} · {op.action} ·{" "}
                                  {op.plans.join(" / ")} · {op.stability}
                                </p>
                              </li>
                            ))}
                        </ul>
                      </details>
                    </section>
                    <ReferenceBoundary>
                      {mounted ? (
                        <React.Suspense fallback={<ReferenceSkeleton />}>
                          <LazyReference
                            snippets={snippets}
                            plan={plan}
                            selected={context.selected}
                          />
                        </React.Suspense>
                      ) : (
                        <p>
                          The interactive schema reference loads in your
                          browser. Endpoint summaries are available above.
                        </p>
                      )}
                    </ReferenceBoundary>
                    <details className="sw-docs-error-guide">
                      <summary>Error recovery and API guidance</summary>
                      <Guide guide={route.guide} />
                    </details>
                  </>
                ) : (
                  <Guide guide={route.guide} />
                )}
                <div className="sw-docs-published">
                  API v1 · <a href="/docs/changelog">Release notes</a> ·{" "}
                  <a
                    href={download}
                    download={DOWNLOAD_FILENAME}
                    onClick={() => trackDocs("developer_openapi_downloaded")}
                  >
                    Generic OpenAPI download
                  </a>
                </div>
              </CopyMetadata.Provider>
            </CodeExamples.Provider>
          </SelectedWorkspace.Provider>
        </main>
        {route.type === "guide" && (
          <aside className="sw-docs-outline" aria-label="On this page">
            <strong>On this page</strong>
            <nav>
              {[...route.guide.markdown.matchAll(/^## (.+)$/gm)].map(
                (match) => (
                  <a
                    key={match[1]}
                    href={`#${headingId(match[1].replace(/`/g, ""))}`}
                  >
                    {match[1].replace(/`/g, "")}
                  </a>
                ),
              )}
            </nav>
          </aside>
        )}
      </div>
      <DocsFooter />
      <dialog
        ref={searchDialog}
        className="sw-docs-search-dialog ph-no-capture ph-no-record"
        aria-labelledby="docs-search-title"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setSearchOpen(false);
          }
        }}
        onCancel={() => setSearchOpen(false)}
        onClose={() => {
          setSearchOpen(false);
          searchRef.current?.focus();
        }}
      >
        <div className="sw-docs-search-controls">
          <div className="sw-docs-search-dialog-header">
            <h2 id="docs-search-title">Search documentation</h2>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              aria-label="Close documentation search"
            >
              Close <span aria-hidden="true">×</span>
            </button>
          </div>
          <form onSubmit={(event) => event.preventDefault()}>
            <label htmlFor="docs-search-detail">
              Search all guides and API details
            </label>
            <input
              id="docs-search-detail"
              type="search"
              value={query}
              maxLength="160"
              autoComplete="off"
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search explanations, parameters and examples…"
            />
          </form>
          <p role="status">
            {query.trim()
              ? `${detailedResults.length} result${detailedResults.length === 1 ? "" : "s"} in guide text and API details`
              : "Enter a word or phrase to search documentation."}
          </p>
          {query.trim() && detailedResults.length === 0 && (
            <p>No matches. Try a workflow, parameter or endpoint name.</p>
          )}
        </div>
        <ul className="sw-docs-detailed-results">
          {detailedResults.map((entry) => (
            <li key={`${entry.kind}:${entry.id}`}>
              <a href={entry.href}>
                <span className="sw-docs-search-result-kind">
                  {entry.kind === "guide" ? "Guide" : "API endpoint"}
                </span>
                <strong>{entry.title}</strong>
                {entry.sectionTitle && (
                  <span className="sw-docs-search-section">
                    {entry.sectionTitle}
                  </span>
                )}
              </a>
              <p className="sw-docs-search-excerpt">
                <DocumentationInline
                  text={entry.excerpt}
                  selected={context.selected}
                />
              </p>
              {entry.kind === "operation" && (
                <span className="sw-docs-search-metadata">
                  <code>
                    {entry.method} {entry.path}
                  </code>
                  <span>
                    Scope: <code>{entry.scope}</code> · Plans:{" "}
                    {entry.plans.join(", ")}
                  </span>
                </span>
              )}
              <a className="sw-docs-search-open" href={entry.href}>
                Open {entry.kind === "guide" ? "guide" : "endpoint"}{" "}
                <span aria-hidden="true">→</span>
              </a>
            </li>
          ))}
        </ul>
      </dialog>
      <CookieConsentRoot />
    </div>
  );
}
